import React, { Fragment, useEffect, useState } from 'react';
import Select from 'react-select';
import CrossIcon from '../../common/icons/CrossIcon';
import { SET_SALE_TRACKING_STATUS } from '../../../store/actions/actionTypes/sales';
import { sendSalesEvent } from '../../../store/actions/sales';
import { Space } from '../../common/Space';
import { getSalesTrackingData } from './SalesTrackingData';
import {
    Card,
    ClearButton,
    FormGroup,
    InfoContainer,
    InfoLabel,
    InfoValue,
    InfoValueNoWrap,
    InputField,
    InputWrapper,
    SelectStyles,
    Separator,
    SubmitButton,
    Subtitle,
    Title
} from './styled/TrainerSalesTrackingStyled';
import { captureException } from '../../../tracker/raven';
import { colors } from '../../../utils/utils';
import useLocalization from '../../../localization/useLocalization';
import Modal, { ModalButton } from '../../Modal/Modal';
import HandClap from '../../common/icons/HandClap';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getAuthToken } from '../../../store/selectors/auth';
import { getProfileEmail, getProfileUserName } from '../../../store/selectors/profile';
import type { RootState } from '../../../store/types';
import type { ComponentProps, ComponentType, ReactNode } from 'react';

type SelectCompatProps = ComponentProps<typeof Select>;
interface InfoValueNoWrapCompatProps {
    $nowrap?: boolean;
    children?: ReactNode;
}
const SelectCompat = Select as ComponentType<SelectCompatProps>;
const InfoValueNoWrapCompat = InfoValueNoWrap as ComponentType<InfoValueNoWrapCompatProps>;

interface SalesOption {
    campaign: string;
    category: string;
    product: string;
}

const filterUnique = <T,>(value: T, index: number, array: T[]) => array.indexOf(value) === index;

function SalesTracking() {
    const localization = useLocalization();
    const dispatch = useAppDispatch();
    const authToken = useAppSelector((state: RootState) => getAuthToken(state) || '');
    const callId = useAppSelector((state: RootState) => state.trainer.callId ?? '');
    const userName = useAppSelector((state: RootState) => getProfileUserName(state) || getProfileEmail(state) || '');
    const trackStatus = useAppSelector((state: RootState) => state.sales?.trackStatus ?? '');

    const [options, setOptions] = useState<SalesOption[]>([]);
    const [campaignRef, setCampaignRef] = React.useState('');
    const [categoryRef, setCategoryRef] = React.useState('');
    const [customerRef, setCustomerRef] = React.useState('');
    const [productRef, setProductRef] = React.useState<SalesOption | null>(null);

    const [modalOpen, setModalOpen] = React.useState(false);

    const isFormValid = !!productRef;

    const trackSale = () => {
        if (!productRef) {
            return;
        }
        dispatch(
            sendSalesEvent({
                eventType: 'sale',
                callId: callId == null ? null : String(callId),
                meta: {
                    campaign: campaignRef || '-',
                    items: [
                        {
                            category: productRef.category,
                            product: productRef.product,
                            amount: 1
                        }
                    ],
                    customer_ref: customerRef
                }
            })
        );
    };

    const currentMinute = new Date().getMinutes();

    useEffect(() => {
        getSalesTrackingData({
            authToken
        })
            .then((o: SalesOption[]) => {
                setOptions(o);
                setCampaignRef(o[0]?.campaign || '');
            })
            .catch(captureException);
    }, [authToken, currentMinute]);

    const close = () => {
        setProductRef(null);
        setCategoryRef('');
        setCustomerRef('');
        dispatch({
            type: SET_SALE_TRACKING_STATUS,
            payload: {
                status: ''
            }
        });
        setModalOpen(false);
    };

    useEffect(() => {
        if (trackStatus !== 'success') {
            return;
        }
        const t = setTimeout(() => close(), 2000);
        return () => {
            clearTimeout(t);
        };
    }, [trackStatus]);

    const selectProps = {
        maxMenuHeight: 150,
        styles: SelectStyles,
        isClearable: true,
        menuPlacement: 'auto' as const
    };

    return (
        <Card>
            <FormGroup>
                <label htmlFor="sales_campaign">{localization.getText('sales.campaign')}</label>
                <SelectCompat
                    value={{
                        value: campaignRef,
                        label: campaignRef
                    }}
                    inputId="sales_campaign"
                    options={options
                        .map((item: SalesOption) => item.campaign)
                        .filter(filterUnique)
                        .map((c: string) => {
                            return { value: c, label: c };
                        })}
                    onChange={(s: { value?: string } | null) => {
                        const newCampaign = s?.value || '';
                        if (newCampaign !== campaignRef) {
                            setCategoryRef('');
                            setProductRef(null);
                        }
                        setCampaignRef(newCampaign);
                    }}
                    {...selectProps}
                />
            </FormGroup>

            <FormGroup>
                <label htmlFor="sales_customer">{localization.getText('sales.customer')}</label>
                <InputWrapper>
                    <InputField
                        id="sales_customer"
                        type="text"
                        value={customerRef}
                        maxLength={32}
                        onChange={e => setCustomerRef(e.target.value)}
                    />
                    {customerRef && (
                        <ClearButton onClick={() => setCustomerRef('')}>
                            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
                            </svg>
                        </ClearButton>
                    )}
                </InputWrapper>
            </FormGroup>

            <FormGroup>
                <label htmlFor="sales_category">{localization.getText('sales.category')}</label>
                <SelectCompat
                    value={{
                        value: categoryRef,
                        label: categoryRef
                    }}
                    inputId="sales_category"
                    options={options
                        .filter((item: SalesOption) => {
                            return !campaignRef || item.campaign === campaignRef;
                        })
                        .map((item: SalesOption) => item.category)
                        .filter(filterUnique)
                        .map((c: string) => {
                            return { value: c, label: c };
                        })}
                    onChange={(s: { value?: string } | null) => {
                        setCategoryRef(s?.value || '');
                        if (productRef?.category !== s?.value) {
                            setProductRef(null);
                        }
                    }}
                    {...selectProps}
                />
            </FormGroup>

            <FormGroup>
                <label htmlFor="sales_product">{localization.getText('sales.product')}</label>
                <SelectCompat
                    value={{
                        label: productRef?.product || '',
                        value: productRef
                    }}
                    inputId="sales_product"
                    options={options
                        .filter((p: SalesOption) => {
                            return !categoryRef || p.category === categoryRef;
                        })
                        .map((p: SalesOption) => {
                            return { value: p, label: p.product };
                        })}
                    onChange={(s: { value?: SalesOption } | null) => {
                        setProductRef(s?.value || null);
                        setCategoryRef(s?.value?.category || '');
                        if (s?.value?.campaign) {
                            setCampaignRef(s?.value?.campaign);
                        }
                    }}
                    {...selectProps}
                />
            </FormGroup>

            <SubmitButton disabled={!isFormValid} onClick={() => setModalOpen(true)}>
                {localization.getText('sales.send')}
            </SubmitButton>

            {modalOpen && (
                <Modal title="" borderRadius="20px" overlayColor="rgba(69, 80, 230, 0.75)" onClose={() => setModalOpen(false)}>
                    {trackStatus === 'error' && (
                        <Fragment>
                            <div
                                style={{
                                    textAlign: 'center'
                                }}>
                                <CrossIcon size={128} />
                            </div>
                            <Space $size={20} />
                            <Title>{localization.getText('sales.modal.error')}</Title>
                            <Space $size={6} />
                            <Subtitle>{localization.getText('sales.modal.try_again')}</Subtitle>
                            <Space $size={6} />
                            <Separator />
                            <ModalButton
                                onClick={() => {
                                    setModalOpen(false);
                                    dispatch({
                                        type: SET_SALE_TRACKING_STATUS,
                                        payload: {
                                            status: ''
                                        }
                                    });
                                }}
                                $color={colors.gray.dark}
                                $isOutline
                                $disabledColor={colors.green.magic_mint}>
                                <span>{localization.getText('sales.close')}</span>
                            </ModalButton>
                        </Fragment>
                    )}
                    {trackStatus === 'loading' && (
                        <div
                            style={{
                                textAlign: 'center'
                            }}>
                            <Space $size={30} />
                            {localization.getText('sales.loading')}
                            <Space $size={30} />
                        </div>
                    )}
                    {trackStatus === 'success' && (
                        <Fragment>
                            <div
                                style={{
                                    textAlign: 'center'
                                }}>
                                <HandClap size={128} />
                            </div>
                            <Space $size={20} />
                            <Title>{localization.getText('sales.modal.good_work')}</Title>
                            <Space $size={6} />
                            <Subtitle>{localization.getText('sales.modal.successfully_uploaded')}</Subtitle>
                            <Space $size={6} />
                            <Separator />
                            <ModalButton
                                onClick={() => close()}
                                $color={colors.gray.dark}
                                $isOutline
                                $disabledColor={colors.green.magic_mint}>
                                <span>{localization.getText('sales.close')}</span>
                            </ModalButton>
                        </Fragment>
                    )}

                    {trackStatus === '' && (
                        <Fragment>
                            <Title>{localization.getText('sales.modal.title')}</Title>
                            <Subtitle>{localization.getText('sales.modal.desc')}</Subtitle>
                            <Separator />
                            <InfoContainer $fontSize="16px">
                                <div>
                                    <InfoLabel>{localization.getText('sales.customer')}</InfoLabel>
                                    <InfoValue>
                                        <b>{customerRef || '–'}</b>
                                    </InfoValue>
                                </div>
                                <div>
                                    <InfoLabel>{localization.getText('sales.campaign')}</InfoLabel>
                                    <InfoValue>
                                        <b>{campaignRef}</b>
                                    </InfoValue>
                                </div>
                                <div>
                                    <InfoLabel>{localization.getText('sales.category')}</InfoLabel>
                                    <InfoValue>
                                        <b>{categoryRef}</b>
                                    </InfoValue>
                                </div>
                                <div>
                                    <InfoLabel>{localization.getText('sales.product')}</InfoLabel>
                                    <InfoValue>
                                        <b>{productRef?.product || ''}</b>
                                    </InfoValue>
                                </div>
                            </InfoContainer>

                            <Separator />

                            <InfoContainer $fontSize="14px">
                                <div>
                                    <InfoLabel>{localization.getText('sales.agent')}</InfoLabel>
                                    <InfoValueNoWrapCompat $nowrap>{userName}</InfoValueNoWrapCompat>
                                </div>
                                <div>
                                    <InfoLabel>{localization.getText('sales.call_id')}</InfoLabel>
                                    <InfoValueNoWrapCompat $nowrap>{callId || '–'}</InfoValueNoWrapCompat>
                                </div>
                                <div>
                                    <InfoLabel>{localization.getText('sales.date_time')}</InfoLabel>
                                    <InfoValue>{new Date().toLocaleString()}</InfoValue>
                                </div>
                            </InfoContainer>

                            <Space $size={6} />

                            <ModalButton
                                onClick={() => trackSale()}
                                $color={colors.green.flashygreen}
                                $disabledColor={colors.green.magic_mint}>
                                <span>{localization.getText('sales.confirm')}</span>
                            </ModalButton>

                            <Space $size={6} />

                            <ModalButton
                                onClick={() => {
                                    setModalOpen(false);
                                }}
                                $color={colors.gray.dark}
                                $isOutline
                                $disabledColor={colors.green.magic_mint}>
                                <span>{localization.getText('sales.cancel')}</span>
                            </ModalButton>
                        </Fragment>
                    )}
                </Modal>
            )}
        </Card>
    );
}

SalesTracking.defaultProps = {};

export default SalesTracking;
